import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {v4 as uuid} from 'uuid';
import _get from 'lodash/get';

import {withStyles} from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Hidden from '@material-ui/core/Hidden';

import {
  HelpAndSupportSidebarAction,
  NotificationBanner,
  RenderOrEmpty,
  AccountSwitcherHeaderAction, OrganizationSwitcherHeaderAction,
  SettingsSidebarAction,
  ProfileSidebarAction,
} from '../index';

import CollapseMenuItem from './CollapseMenuItem';
import MenuItem from './MenuItem';
import SidebarWrapper from './SidebarWrapper';

import style from './style';
import {ROLES_AVAILABILITY, USER_ROLES} from '../../constants';
import SidebarBottomElements from './SidebarBottomElements';

class Sidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      heightBelowMd: false,
    };
  }

  componentDidMount() {
    this.checkScreenHeight();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    this.checkScreenHeight();
  };

  checkScreenHeight = () => {
    const height = window.innerHeight;
    this.setState({
      heightBelowMd: height < 800,
    });
  };

  getNavLinks = (routes) => {
    const {
      classes,
      loggedAccount,
    } = this.props;
    return (
      routes.map((item) => {
        const isAvailable = _get(ROLES_AVAILABILITY, [item.name, loggedAccount.role], true);
        if (!isAvailable) return null;

        const isActive = this.isActiveRoute(item.path);
        if (item.redirect) {
          return null;
        }
        if (item.collapse) {
          return (
            <CollapseMenuItem
              classes={classes}
              isActive={isActive}
              item={item}
              key={uuid()}
              openCollapse={this.openCollapse}
            />
          );
        }
        return (
          <MenuItem
            classes={classes}
            isActive={isActive}
            item={item}
            key={uuid()}
            isBottom={!!item.bottom}
          />
        );
      })
    );
  };

  getLogo = (miniActive) => {
    const {
      classes,
      logo,
      logoMini,
    } = this.props;
    return (
      <a
        className={classNames(
          classes.logo,
          {
            [classes.logo_mini]: miniActive,
          },
        )}
        href="/"
      >
        <RenderOrEmpty isShow={!miniActive}>
          <img
            src={logo}
            alt="logo"
            className={classes.logo__img}
          />
        </RenderOrEmpty>
        <RenderOrEmpty isShow={miniActive}>
          <img
            src={logoMini}
            alt="logo_mini"
            className={classes.logo__img_mini}
          />
        </RenderOrEmpty>
      </a>
    );
  };

  openCollapse = (collapse) => {
    this.setState((prevState) => ({
      [collapse]: !prevState[collapse],
    }));
  };

  isActiveRoute = (routeName) => this.props.location.pathname.indexOf(routeName) > -1;

  render() {
    const {
      classes,
      isOpen,
      routes,
      loggedAccount,
      accountInfo,
      currentOrganization,
      notificationBannerData,
      userAccounts,
      userOrganizations,
    } = this.props;
    const organizationsAllowed = _get(accountInfo, 'organizationsAllowed', false);
    const userRole = loggedAccount.role;

    const modalProps = {
      keepMounted: true,
    };

    const drawerClasses = {
      paper: classes.drawerPaper,
    };
    const sidebarWrapperClassName = classNames(
      classes.sidebarWrapper,
      {
        [classes.drawerPaper_mini]: this.props.miniActive,
        [classes.sidebarWrapperWithPerfectScrollbar]:
        navigator.platform.indexOf('Win') > -1,
      },
    );
    const topNavLinks = this.getNavLinks(routes.filter((r) => !r.bottom));
    const bottomNavLinks = this.getNavLinks(routes.filter((r) => r.bottom));

    return (
      <>
        <Hidden mdUp={true}>
          <Drawer
            variant="temporary"
            anchor="right"
            open={isOpen}
            classes={drawerClasses}
            onClose={this.props.handleDrawerToggle}
            ModalProps={modalProps}
          >
            {this.getLogo(false)}
            <SidebarWrapper className={sidebarWrapperClassName}>
              <NotificationBanner notificationData={notificationBannerData} />
              <AccountSwitcherHeaderAction
                loggedAccount={loggedAccount}
                placement="sidebar"
                switchAccount={this.props.switchAccount}
                userAccounts={userAccounts}
              />
              <RenderOrEmpty isShow={organizationsAllowed}>
                <OrganizationSwitcherHeaderAction
                  currentOrganization={currentOrganization}
                  placement="sidebar"
                  switchOrganization={this.props.switchOrganization}
                  userOrganizations={userOrganizations}
                  loggedAccount={loggedAccount}
                />
              </RenderOrEmpty>
              <div className={classes.sectionDivider} />
              <ProfileSidebarAction
                logout={this.props.logout}
                loggedAccount={loggedAccount}
              />
              <RenderOrEmpty
                isShow={userRole !== USER_ROLES.VIEWER && userRole !== USER_ROLES.SERVICE_DESK}
              >
                <SettingsSidebarAction accountInfo={accountInfo} />
              </RenderOrEmpty>
              <HelpAndSupportSidebarAction />
              <div className={classes.sectionDivider} />
              <List className={classes.navLinks}>
                {this.getNavLinks(routes)}
              </List>
            </SidebarWrapper>
          </Drawer>
        </Hidden>
        <Hidden smDown={true}>
          <div
            className={`${classes.drawerPaper} ${classes.drawerPaper_mini}`}
            open={true}
          >
            {this.getLogo(true)}
            <SidebarWrapper className={sidebarWrapperClassName}>
              <List className={classes.navLinks}>
                {topNavLinks}
              </List>
              <SidebarBottomElements
                bottomNavLinks={bottomNavLinks}
                accountInfo={accountInfo}
                userRole={userRole}
                containerClass={classes.sidebar__bottomWrapper}
              />
            </SidebarWrapper>
          </div>
        </Hidden>
      </>
    );
  }
}

Sidebar.propTypes = {
  classes: PropTypes.object.isRequired,
  isOpen: PropTypes.bool,
  location: PropTypes.object,
  loggedAccount: PropTypes.object.isRequired,
  accountInfo: PropTypes.object.isRequired,
  currentOrganization: PropTypes.object,
  logo: PropTypes.string,
  logoMini: PropTypes.string,
  miniActive: PropTypes.bool.isRequired,
  notificationBannerData: PropTypes.object.isRequired,
  routes: PropTypes.array.isRequired,
  userAccounts: PropTypes.array.isRequired,
  userOrganizations: PropTypes.array.isRequired,

  handleDrawerToggle: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  switchAccount: PropTypes.func.isRequired,
  switchOrganization: PropTypes.func.isRequired,
};

Sidebar.defaultProps = {
  isOpen: false,
  location: {
    pathname: '',
  },
  logo: undefined,
  logoMini: undefined,
  currentOrganization: null,
};

export default withStyles(style)(Sidebar);
